import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { Icon } from '@chakra-ui/react'

// eslint-disable-next-line no-restricted-imports
import { ruleEventTypeOptions } from '@/features/incident-rules/utils/utils'
import { IncidentType, RuleEventType } from '@/graphql/generated/schemas'
import { IncidentTypeSlug } from '@/utils/incidents'

import { Incident } from '../../Incident'
import { Alarm } from './Alarm'
import { DeviceHealthIncident } from './DeviceHealthIncident'
import { DoorForced } from './DoorForced'
import { DoorHeldOpen } from './DoorHeldOpen'
import { LineCrossing } from './LineCrossing'
import { ManualIncident } from './ManualIncident'
import { TailgatingIncident } from './TailgatingIncident'

interface IncidentIconIProps {
  incidentType?: IncidentType | null
  isManualIncident?: boolean
  size?: number
  name?: string
  slug?: string | null
}

const normalizeString = (str: string | null | undefined): string => {
  if (!str) return ''
  return str.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim()
}

export const getIconBySlug = (slug: string) => {
  switch (slug) {
    case IncidentTypeSlug.DoorForcedOpen:
      return DoorForced
    case IncidentTypeSlug.DoorUnlockedTailgate:
      return TailgatingIncident
    case IncidentTypeSlug.DeviceHealth:
      return DeviceHealthIncident
    case IncidentTypeSlug.DoorHeldOpenStarted:
      return DoorHeldOpen
    case IncidentTypeSlug.LineCrossing:
      return LineCrossing
    case IncidentTypeSlug.DoorOpenAlarm:
      return DoorHeldOpen
    case IncidentTypeSlug.Alarm:
      return Alarm
    default:
      return null
  }
}

export const IncidentIcon = ({
  incidentType,
  isManualIncident = false,
  size = 50,
  name = null,
  slug = null,
}: IncidentIconIProps) => {
  const isIncidentTypeDefinition = useFeatureFlag(
    FeatureFlag.useIncidentTypeDefinition
  )

  const iconStyleProps = {
    boxSize: `${size}px`,
    strokeColor: '#fff',
    backgroundColor: 'brand.100',
    bgGradient: 'linear(to-t, brand.100, brand.200)',
    padding: `${Math.floor(size / 10)}px`,
    borderRadius: 'base',
  }

  if (isIncidentTypeDefinition && slug != null) {
    const slugIcon = getIconBySlug(slug)
    if (slugIcon !== null) {
      return <Icon as={slugIcon} {...iconStyleProps} />
    }
  }

  const doorOpenAlarmOption = ruleEventTypeOptions.find(
    (option) => option.value === RuleEventType.DoorOpenAlarm
  )

  switch (incidentType) {
    case IncidentType.DeviceHealth:
      return <Icon as={DeviceHealthIncident} {...iconStyleProps} />
    case IncidentType.ForcedEntry:
      return <Icon as={DoorForced} {...iconStyleProps} />
    case IncidentType.Tailgating:
      return <Icon as={TailgatingIncident} {...iconStyleProps} />
    case IncidentType.DoorHeldOpen:
      return <Icon as={DoorHeldOpen} {...iconStyleProps} />
    case IncidentType.DoorHeldClose:
      return <Icon as={DoorHeldOpen} {...iconStyleProps} />
    case IncidentType.LineCrossing:
      return <Icon as={LineCrossing} {...iconStyleProps} />
    case IncidentType.Alarm:
      // Door Open Alarms should use the 'DoorHeldOpen' icon
      if (
        normalizeString(name) === normalizeString(doorOpenAlarmOption?.label)
      ) {
        return <Icon as={DoorHeldOpen} {...iconStyleProps} />
      }
      return <Icon as={Alarm} {...iconStyleProps} />
    case null:
      // Typeless non-manual incidents (e.g. dynamic incident types with no
      // slug mapping) fall back to the generic incident icon so every row
      // renders an icon
      return isManualIncident ? (
        <Icon as={ManualIncident} {...iconStyleProps} />
      ) : (
        <Icon as={Incident} {...iconStyleProps} />
      )
    default:
      return <Icon as={ManualIncident} {...iconStyleProps} />
  }
}
